import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import axiosInstance from '../../api/axiosInstance';
import './SystemManagePage.css';

// ── 복구 절차 모달 ────────────────────────────────────────────
const RestoreModal = ({ config, onClose }) => {
  const [copiedKey, setCopiedKey] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1000);
  };

  const localPath   = config?.BACKUP_LOCAL_PATH   || 'C:\\stock_project\\backup';
  const networkPath = config?.BACKUP_NETWORK_PATH || '';

  const CmdLine = ({ text, id }) => (
    <div className="sm-cmd-line">
      <code>{text}</code>
      <button
        className={`sm-copy-btn${copiedKey === id ? ' copied' : ''}`}
        onClick={() => copy(text, id)}
      >
        {copiedKey === id ? '복사됨!' : '📋'}
      </button>
    </div>
  );

  return (
    <div className="sm-modal-overlay" onClick={onClose}>
      <div className="sm-modal" onClick={e => e.stopPropagation()}>
        <div className="sm-modal-header">
          <span>DB 복구 절차</span>
          <button className="sm-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="sm-modal-body">
          <div className="sm-restore-warn">
            <strong>⚠️ 주의사항</strong>
            <p>복구 시 현재 데이터가 모두 덮어씌워집니다. 신중하게 진행하세요.</p>
          </div>

          <div className="sm-restore-section">
            <h4>백업 파일 위치</h4>
            <ul>
              <li><strong>로컬:</strong> {localPath}</li>
              {networkPath && <li><strong>IPDISK:</strong> {networkPath}</li>}
            </ul>
          </div>

          <div className="sm-restore-section">
            <h4>복구 절차</h4>

            <div className="sm-restore-step">
              <div className="sm-step-num">1</div>
              <div className="sm-step-body">
                <div className="sm-step-title">백엔드 서버 중지</div>
                <CmdLine text="taskkill /f /im python.exe" id="cmd1" />
              </div>
            </div>

            <div className="sm-restore-step">
              <div className="sm-step-num">2</div>
              <div className="sm-step-body">
                <div className="sm-step-title">기존 DB 삭제</div>
                <CmdLine text="dropdb -U postgres stock_db" id="cmd2" />
              </div>
            </div>

            <div className="sm-restore-step">
              <div className="sm-step-num">3</div>
              <div className="sm-step-body">
                <div className="sm-step-title">DB 재생성</div>
                <CmdLine text="createdb -U postgres stock_db" id="cmd3" />
              </div>
            </div>

            <div className="sm-restore-step">
              <div className="sm-step-num">4</div>
              <div className="sm-step-body">
                <div className="sm-step-title">백업 파일로 복구</div>
                <CmdLine text="psql -U postgres -d stock_db -f [백업파일 전체경로]" id="cmd4" />
                <div className="sm-step-example">
                  예시: psql -U postgres -d stock_db -f {localPath}\stock_db_20260614_020000.sql
                </div>
              </div>
            </div>

            <div className="sm-restore-step">
              <div className="sm-step-num">5</div>
              <div className="sm-step-body">
                <div className="sm-step-title">백엔드 서버 재시작</div>
                <CmdLine text="cd C:\stock_project" id="cmd5a" />
                <CmdLine text="start_backend"          id="cmd5b" />
              </div>
            </div>
          </div>

          <div className="sm-restore-tip">
            💡 복구 후 시세수집을 한번 실행하시면 최신 데이터가 반영됩니다.
          </div>
        </div>

        <div className="sm-modal-footer">
          <button className="sm-btn sm-btn-secondary" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
};

// ── 메인 콘텐츠 ──────────────────────────────────────────────
const BackupContent = ({ isActive }) => {
  // 설정
  const [config,    setConfig]    = useState({});
  const [schedHour, setSchedHour] = useState('2');
  const [schedMin,  setSchedMin]  = useState('0');

  // 즉시 백업
  const [isBackingUp,  setIsBackingUp]  = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  // 이력
  const [history,     setHistory]     = useState([]);
  const [histTotal,   setHistTotal]   = useState(0);
  const [histPage,    setHistPage]    = useState(1);
  const [histLoading, setHistLoading] = useState(false);

  // UI
  const [showModal, setShowModal] = useState(false);
  const [saveMsg,   setSaveMsg]   = useState(null);
  const [message,   setMessage]   = useState(null);

  // 드래그
  const [leftWidth,  setLeftWidth]  = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const LIMIT = 20;

  // ── 드래그 핸들러 ──
  const handleMouseDown = () => setIsDragging(true);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const container = containerRef.current;
      if (!container) return;
      const rect    = container.getBoundingClientRect();
      const newLeft = ((e.clientX - rect.left) / rect.width) * 100;
      if (newLeft >= 30 && newLeft <= 80) {
        setLeftWidth(newLeft);
      }
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup',   handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup',   handleMouseUp);
    };
  }, [isDragging]);

  // ── 데이터 로드 ──
  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/system/backup-config');
      setConfig(data);
      const parts = (data.BACKUP_SCHEDULE || '0 2 * * *').split(' ');
      setSchedMin(parts[0]  || '0');
      setSchedHour(parts[1] || '2');
    } catch (err) {
      console.error('[SystemManage] 설정 로드 실패:', err);
    }
  }, []);

  const loadHistory = useCallback(async (page) => {
    setHistLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/system/backup-history?page=${page}&limit=${LIMIT}`
      );
      setHistory(data.items  || []);
      setHistTotal(data.total || 0);
    } catch (err) {
      console.error('[SystemManage] 이력 로드 실패:', err);
    } finally {
      setHistLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      loadConfig();
      loadHistory(1);
    }
  }, [isActive, loadConfig, loadHistory]);

  // ── 즉시 백업 ──
  const handleBackupNow = async () => {
    setIsBackingUp(true);
    setBackupResult(null);
    try {
      const { data } = await axiosInstance.post('/system/backup', {}, { timeout: 120000 });
      setBackupResult({ ok: true, data });
      loadHistory(1);
      setHistPage(1);
    } catch (err) {
      const msg = err.response?.data?.detail || '백업 실행 중 오류가 발생했습니다.';
      setBackupResult({ ok: false, msg });
    } finally {
      setIsBackingUp(false);
    }
  };

  // ── 설정 저장 ──
  const handleSaveConfig = async () => {
    const cron = `${schedMin} ${schedHour} * * *`;
    const body = {
      BACKUP_LOCAL_PATH:     config.BACKUP_LOCAL_PATH     || '',
      BACKUP_NETWORK_PATH:   config.BACKUP_NETWORK_PATH   || '',
      BACKUP_RETENTION_DAYS: String(config.BACKUP_RETENTION_DAYS || '30'),
      BACKUP_SCHEDULE_USE:   config.BACKUP_SCHEDULE_USE   || 'N',
      BACKUP_SCHEDULE:       cron,
    };
    try {
      await axiosInstance.put('/system/backup-config', body);
      setSaveMsg('success');
      setTimeout(() => setSaveMsg(null), 3000);
      loadConfig();
    } catch (err) {
      setSaveMsg('error');
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  // ── 파일 다운로드 ──
  const handleDownload = async (id, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`/api/system/backup/download/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: `다운로드 실패: ${err.message}` });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const totalPages = Math.ceil(histTotal / LIMIT);
  const fmtSize = (kb) => {
    if (!kb && kb !== 0) return '-';
    if (kb >= 1024) return `${(kb / 1024).toFixed(1)}MB`;
    return `${kb}KB`;
  };

  return (
    <PageLayout title="시스템관리" message={message}>
      {/* ── 좌우 분할 컨테이너 ── */}
      <div
        className={`sm-split${isDragging ? ' sm-split-dragging' : ''}`}
        ref={containerRef}
      >
        {/* ── 왼쪽 패널 ── */}
        <div className="sm-left" style={{ width: `${leftWidth}%` }}>

          {/* 백업 관리 카드 */}
          <div className="sm-card">
            <div className="sm-card-header">백업 관리</div>

            {/* ── 즉시 백업 섹션 ── */}
            <div className="sm-sub">
              <div className="sm-sub-title">📦 즉시 백업</div>

              <button
                className="sm-btn sm-btn-primary sm-btn-backup"
                onClick={handleBackupNow}
                disabled={isBackingUp}
              >
                {isBackingUp
                  ? <><span className="sm-spinner" /> 백업 중...</>
                  : '💾 지금 백업 실행'}
              </button>

              {isBackingUp && (
                <div className="sm-backup-status">백업 중입니다...</div>
              )}

              {backupResult && !isBackingUp && (
                <div className={`sm-backup-result ${backupResult.ok ? 'ok' : 'fail'}`}>
                  {backupResult.ok ? (
                    <>
                      <div>✅ 백업 완료</div>
                      <div>파일명: {backupResult.data.filename}</div>
                      <div>크기: {fmtSize(backupResult.data.file_size_kb)}</div>
                      <div>
                        로컬: {backupResult.data.local_success   ? '✅' : '❌'}
                        &nbsp; IPDISK: {backupResult.data.network_success ? '✅' : '❌'}
                        {backupResult.data.error_msg && (
                          <span className="sm-net-error"> — {backupResult.data.error_msg}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div>❌ 백업 실패: {backupResult.msg}</div>
                  )}
                </div>
              )}

              <div className="sm-backup-info">
                <div><span className="sm-lbl">백업 대상:</span> stock_db (PostgreSQL)</div>
                <div><span className="sm-lbl">로컬:</span> {config.BACKUP_LOCAL_PATH || '-'}</div>
                <div><span className="sm-lbl">IPDISK:</span> {config.BACKUP_NETWORK_PATH || '-'}</div>
              </div>
            </div>

            <div className="sm-card-divider" />

            {/* ── 자동 백업 설정 섹션 ── */}
            <div className="sm-sub">
              <div className="sm-sub-title">⏰ 자동 백업 설정</div>

              {/* 토글 + 저장 버튼 같은 줄 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="sm-lbl" style={{ minWidth: 'unset' }}>자동 백업 사용</span>
                  <label className="sm-toggle">
                    <input
                      type="checkbox"
                      checked={config.BACKUP_SCHEDULE_USE === 'Y'}
                      onChange={e =>
                        setConfig(c => ({ ...c, BACKUP_SCHEDULE_USE: e.target.checked ? 'Y' : 'N' }))
                      }
                    />
                    <span className="sm-toggle-slider" />
                    <span className="sm-toggle-text">
                      {config.BACKUP_SCHEDULE_USE === 'Y' ? 'ON' : 'OFF'}
                    </span>
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button className="sm-btn sm-btn-primary" onClick={handleSaveConfig}>저장</button>
                  {saveMsg === 'success' && <span className="sm-save-ok">✅ 저장되었습니다.</span>}
                  {saveMsg === 'error'   && <span className="sm-save-err">❌ 저장 실패</span>}
                </div>
              </div>

              <div className="sm-row">
                <span className="sm-lbl">백업 시각</span>
                <div className="sm-inline">
                  <input
                    type="number"
                    className="sm-input sm-input-sm"
                    value={schedHour}
                    min="0" max="23"
                    onChange={e => setSchedHour(e.target.value)}
                  />
                  <span className="sm-unit">시</span>
                  <select
                    className="sm-select"
                    value={schedMin}
                    onChange={e => setSchedMin(e.target.value)}
                  >
                    <option value="0">00</option>
                    <option value="15">15</option>
                    <option value="30">30</option>
                    <option value="45">45</option>
                  </select>
                  <span className="sm-unit">분</span>
                  <span className="sm-hint">매일 지정 시각에 자동 백업</span>
                </div>
              </div>

              <div className="sm-row">
                <span className="sm-lbl">보관 기간</span>
                <div className="sm-inline">
                  <input
                    type="number"
                    className="sm-input sm-input-sm"
                    value={config.BACKUP_RETENTION_DAYS || '30'}
                    min="1" max="365"
                    onChange={e =>
                      setConfig(c => ({ ...c, BACKUP_RETENTION_DAYS: e.target.value }))
                    }
                  />
                  <span className="sm-unit">일</span>
                  <span className="sm-hint">설정 기간 초과 시 자동 삭제</span>
                </div>
              </div>

              <div className="sm-row sm-row-col">
                <span className="sm-lbl">로컬 경로</span>
                <input
                  type="text"
                  className="sm-input sm-input-full"
                  value={config.BACKUP_LOCAL_PATH || ''}
                  onChange={e =>
                    setConfig(c => ({ ...c, BACKUP_LOCAL_PATH: e.target.value }))
                  }
                />
              </div>

              <div className="sm-row sm-row-col">
                <span className="sm-lbl">IPDISK 경로</span>
                <input
                  type="text"
                  className="sm-input sm-input-full"
                  value={config.BACKUP_NETWORK_PATH || ''}
                  onChange={e =>
                    setConfig(c => ({ ...c, BACKUP_NETWORK_PATH: e.target.value }))
                  }
                />
              </div>

            </div>

            <div className="sm-card-divider" />

            {/* ── DB 복구 방법 섹션 ── */}
            <div className="sm-sub">
              <div className="sm-sub-title">⚠️ DB 복구 방법</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  className="sm-btn sm-btn-secondary"
                  onClick={() => setShowModal(true)}
                >
                  📋 복구 절차 보기
                </button>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  데이터 복구가 필요한 경우 클릭하세요.
                </span>
              </div>
            </div>
          </div>

          {/* 백업 이력 카드 */}
          <div className="sm-card">
            <div className="sm-card-header sm-card-header-row">
              <span>백업 이력</span>
              <button
                className="sm-refresh-btn"
                onClick={() => loadHistory(histPage)}
              >
                🔄 새로고침
              </button>
            </div>

            {histLoading ? (
              <div className="sm-loading">로딩 중...</div>
            ) : (
              <>
                <div className="sm-table-wrap">
                  <table className="sm-table">
                    <thead>
                      <tr>
                        <th>백업일시</th>
                        <th>유형</th>
                        <th>파일명</th>
                        <th>크기</th>
                        <th>로컬</th>
                        <th>IPDISK</th>
                        <th>다운로드</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="sm-empty">백업 이력이 없습니다.</td>
                        </tr>
                      ) : history.map(row => {
                        const bothFail = !row.local_success && !row.network_success;
                        return (
                          <tr key={row.id} className={bothFail ? 'sm-row-fail' : ''}>
                            <td className="sm-col-dt">{row.backup_dt}</td>
                            <td>
                              <span className={`sm-badge ${row.backup_type === 'AUTO' ? 'auto' : 'manual'}`}>
                                {row.backup_type === 'AUTO' ? '자동' : '수동'}
                              </span>
                            </td>
                            <td className="sm-col-file" title={row.backup_filename}>
                              {row.backup_filename}
                            </td>
                            <td className="sm-col-r">{fmtSize(row.file_size_kb)}</td>
                            <td className="sm-col-c">{row.local_success   ? '✅' : '❌'}</td>
                            <td className="sm-col-c">{row.network_success ? '✅' : '❌'}</td>
                            <td className="sm-col-c">
                              <button
                                className="sm-dl-btn"
                                disabled={!row.local_success}
                                onClick={() => handleDownload(row.id, row.backup_filename)}
                                title={row.local_success ? '다운로드' : '로컬 파일 없음'}
                              >
                                ⬇ 다운로드
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="sm-pagination">
                    <button
                      disabled={histPage <= 1}
                      onClick={() => { const p = histPage - 1; setHistPage(p); loadHistory(p); }}
                    >◀ 이전</button>
                    <span>{histPage} / {totalPages} 페이지 (총 {histTotal}건)</span>
                    <button
                      disabled={histPage >= totalPages}
                      onClick={() => { const p = histPage + 1; setHistPage(p); loadHistory(p); }}
                    >다음 ▶</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── 드래그 구분선 ── */}
        <div
          className={`sm-divider${isDragging ? ' dragging' : ''}`}
          onMouseDown={handleMouseDown}
        />

        {/* ── 오른쪽 패널 ── */}
        <div className="sm-right">
          <span className="sm-right-placeholder">추후 기능이 추가될 예정입니다.</span>
        </div>
      </div>

      {showModal && (
        <RestoreModal config={config} onClose={() => setShowModal(false)} />
      )}
    </PageLayout>
  );
};

// ── 최상위 컴포넌트 (관리자 체크) ────────────────────────────
const SystemManagePage = ({ isActive }) => {
  const { user } = useAuth();

  if (!user?.is_admin) {
    return (
      <PageLayout title="시스템관리">
        <div className="sm-no-access">관리자만 접근 가능한 화면입니다.</div>
      </PageLayout>
    );
  }

  return <BackupContent isActive={isActive} />;
};

export default SystemManagePage;
